import React, { createContext, useState, useContext, useEffect } from 'react';

const AppContext = createContext();

export const AppProvider = ({ children }) => {
    const [theme, setTheme] = useState(localStorage.getItem('theme') || 'light');
    const [language, setLanguage] = useState(localStorage.getItem('language') || 'en');
    const [notificationsEnabled, setNotificationsEnabled] = useState(JSON.parse(localStorage.getItem('notificationsEnabled')) ?? true);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);

    const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

    useEffect(() => {
        localStorage.setItem('theme', theme);
        if (theme === 'dark') {
            document.documentElement.classList.add('dark');
        } else {
            document.documentElement.classList.remove('dark');
        }
    }, [theme]);

    useEffect(() => {
        localStorage.setItem('language', language);
    }, [language]);

    useEffect(() => {
        localStorage.setItem('notificationsEnabled', JSON.stringify(notificationsEnabled));
    }, [notificationsEnabled]);

    const toggleTheme = () => setTheme(prev => prev === 'light' ? 'dark' : 'light');

    const translations = {
        en: {
            welcome: "Hi",
            welcome_back: "Welcome Back!",
            sub_welcome: "Manage your complaints and track status in real-time.",
            home: "Home",
            dashboard: "Dashboard",
            complaints: "Complaints",
            reports: "Reports",
            settings: "Settings",
            raise_complaint: "Raise Complaints",
            profile: "Profile",
            recent_activity: "Recent Activity",
            view_all: "View All",
            total_raised: "Total Raised",
            pending: "Pending",
            resolved: "Resolved",
            in_progress: "In Progress",
            admin_console: "Admin Console",
            admin_subtitle: "Manage tickets, users and global system parameters.",
            global_ticket_stream: "Global Ticket Stream",
            ticket_stream_desc: "Real-time feed of all registered complaints across departments.",
            operation_controls: "Operation Controls",
            system_gov: "System Governance",
            system_gov_desc: "Manage roles, categories and locations.",
            service_overview: "Service Overview",
            uptime: "Uptime",
            load: "Load",
            security_guard_active: "Security Guard Active",
            infrastructure_integrity: "Infrastructure integrity is verified. Real-time encryption for all data transactions is enabled.",
            generate_report: "Generate Outcome Report",
            report_subject: "Report Subject",
            recipient_email: "Recipient Email",
            dispatch_report: "Dispatch Official Report",
            distribution_trends: "Distribution Trends",
            avg_resolution: "Average Resolution",
            server_latency: "Server Latency",
            system_audit: "System Audit",
            security_trail_desc: "Real-time security trail and administrative activity tracking.",
            satisfaction_rate: "Satisfaction Rate",
            satisfaction_rate_lbl: "Satisfaction rate based on user feedback.",
            fetching_data: "Fetching data...",
            no_activity: "No activity recorded yet.",
            volume_vs_last_month: "volume vs last month",
            priority_resolution: "Priority resolution",
            active: "Active",
            minimal: "Minimal",
            optimal: "Optimal",
            low_cap: "Low",
            security_trail_extended_desc: "Real-time security trail and administrative activity tracking.",
            fetching_security_logs: "Fetching security logs...",
            no_audit_logs_found: "No Audit Logs Found",
            no_security_events_match: "No security events match your current filter criteria.",
            system_service_account: "Service Account",
            internal_ip: "Internal",
            unknown_user: "Unknown",
            management_reports: "Management Reports",
            official_res_documents_desc: "Access your official resolution documents and certificates.",
            reports_count: "Reports",
            send_res_report: "Send Resolution Report",
            admin_panel: "Admin Panel",
            select_task: "Select Task",
            select_completed_complaint: "Select a completed complaint...",
            report_sent_to_user_desc: "The report will be sent to this user's dashboard.",
            report_overview: "Report Overview",
            certifies_resolution_desc: "This document certifies the successful resolution of the service request detailed below.",
            recipient_info: "Recipient Information",
            task_reference: "Task Reference",
            date_generated: "Date Generated",
            verified_by: "Verified By",
            download_official_pdf: "Download Official PDF",
            completed: "Completed",
            notifications: "Notifications",
            no_notifications: "No new updates.",
            search_placeholder: "Search complaints...",
            my_quick_stats: "My Quick Stats",
            profile_settings: "Profile Settings",
            display_language: "Display & Language",
            logout: "Logout",
            save_changes: "Save Changes",
            theme_mode: "Theme Mode",
            theme_desc: "Switch between Dark and Light mode.",
            app_language: "App Language",
            lang_desc: "Choose your preferred language.",
            profile_info: "Profile Information",
            full_name: "Full Name",
            email_address: "Email Address",
            bio: "Bio",
            change_photo: "Change Photo",
            remove_photo: "Remove Photo",
            upload_photo: "Upload Photo",
            remove_bio: "Clear Bio",
            email_notif: "Email Notifications",
            notif_desc: "Receive updates about your complaints via email.",
            push_notif: "Push Notifications",
            push_desc: "Get real-time alerts in your browser.",
            raise_new_title: "Raise a New Complaint",
            subject_title: "Subject / Title",
            subject_placeholder: "e.g. Printer not working",
            complaint_type: "Complaint Type",
            location_block: "Location / Block",
            location_placeholder: "e.g. Block A-102",
            department: "Department Name",
            dept_placeholder: "e.g. IT Department",
            room_no: "Room No",
            room_placeholder: "e.g. 101",
            description: "Description",
            desc_placeholder: "Describe the issue in detail...",
            submit_complaint: "Submit Complaint",
            back_to_dashboard: "Back to Dashboard",
            raise_new_desc: "Provide detailed information to help our technical staff resolve your issue faster.",
            ticket_id_lbl: "TICKET ID",
            attachment_lbl: "Attachment (Images/PDF)",
            drop_file_hint: "Drop file here or click to browse",
            max_file_size: "Max size: 5MB • Formats: JPG, PNG, PDF",
            processing: "Processing...",
            submit_request: "Submit Support Request",
            response_time_sla: "Response Time SLA",
            urgent_priority: "Urgent Priority",
            high_priority: "High Priority",
            medium_priority: "Medium Priority",
            quality_guarantee: "Quality Guarantee",
            quality_verif: "Verification of resolution by the raising user.",
            quality_escalation: "Automated escalation for delayed requests.",
            quality_log: "Transparent activity log for every action.",
            hours: "Hours",
            all_complaints: "All Complaints",
            details: "Details",
            linked_to: "Linked To",
            quick_setup: "Quick Setup",
            records: "Records",
            unlinked: "Unlinked",
            alias: "Alias",
            save_updates: "Save Updates",
            confirm_registration: "Confirm Registration",
            edit_item: "Edit",
            add_item: "Add",
            room_number_lbl: "Room Number",
            block_name_lbl: "Block Name / Code",
            programme_name_lbl: "Programme Name",
            optional_desc_lbl: "Optional Description",
            select_parent: "Select Parent...",
            no_complaints_found: "No complaints found",
            id: "ID",
            type: "Type",
            location: "Location",
            status: "Status",
            assigned_to: "Assigned To",
            date: "Date",
            actions: "Actions",
            system_reports: "System Reports",
            new_report: "New Report",
            monthly_report: "Monthly Resolution Report",
            dept_efficiency: "Departmental Efficiency",
            audit_summary: "Hardware Audit Summary",
            user_survey: "User Satisfaction Survey",
            generated_on: "Generated on",
            format: "Format",
            total_complaints: "Total Complaints",
            active_users: "Active Users",
            system_audit: "System Audit Logs",
            average_rating: "Avg. Rating",
            // Sidebar & Nav
            master_data: "Master Data",
            audit_logs: "Audit Logs",
            user_management: "User Management",
            global_directory: "Global User Directory",
            system_user_mgmt: "System User Management",
            broadcast: "Broadcast",
            system_broadcast: "System Broadcast",
            broadcast_desc: "Send a message to all users in the system.",
            message_content: "Message Content",
            message_placeholder: "Type your message here...",
            send_to_all_users: "Send to All Users",
            user_mgmt_desc: "Centralized control for all staff and member accounts.",
            search_directory: "Search directory...",
            identity_name: "Full Identity Name",
            phone_optional: "Phone (Optional)",
            email_access: "System Email Access",
            security_role: "Security Role",
            assigned_dept: "Assigned Dept",
            temp_password: "Temporary Password",
            update_directory: "Update Global Directory",
            finalize_reg: "Finalize Registration",
            // Dashboards
            staff_portal: "Staff Resolution Portal",
            support_workspace: "Support Workspace",
            admin_control: "Administrative Control Center",
            admin_console: "Admin Console",
            admin_subtitle: "Central processing hub for monitoring system tickets and managing support operations.",
            global_ticket_stream: "Global Ticket Stream",
            ticket_stream_desc: "Live feed of all incoming support requests across sectors.",
            operation_controls: "Operation Controls",
            manage_users_desc: "Configure system access rights",
            system_gov: "System Governance",
            system_gov_desc: "Audit logs and security policies",
            service_overview: "Service Overview",
            uptime: "UPTIME",
            load: "LOAD",
            review_all: "Review All",
            fetching_ticket_data: "Fetching ticket data...",
            no_activity_recorded: "No activity recorded yet.",
            admin_perf_desc: "System performance is currently optimal with a 98.4% resolution rate within SLA guidelines.",
            user_intelligence: "User Intelligence Hub",
            auth_with_server: "Authenticating with data server...",
            no_segment_tickets: "No active tickets recorded for this segment.",
            init_first_ticket: "Initialize Your First Ticket",
            user_res_desc: "Deploy critical hardware and infrastructure issues to our specialized technical squadron for immediate triage.",
            raise_ticket: "Raise Ticket",
            edit_profile_btn: "Edit Profile",
            need_help: "Need Help?",
            raise_ticket_desc: "Create a new support ticket",
            track_res_desc: "Track resolution progress",
            manage_personal_desc: "Manage your personal info",
            read_faq_desc: "Read FAQs and guides",
            master_control: "Master Control",
            system_load: "System Load",
            capacity_analysis: "Capacity Analysis",
            activity_feed: "Activity Feed",
            // Register Page
            create_account: "Create Account",
            join_system: "Join the Ticket Management System",
            already_have_account: "Already have an account?",
            login_here: "Login here",
            // General
            welcome: "Hi",
            welcome_back: "Welcome Back!",
            sub_welcome: "Manage your complaints and track status in real-time.",
            email_lbl: "Email Address",
            password_lbl: "Password",
            login_failed: "Login failed",
            login_hint: "Need an account? Please contact your System Administrator.",
            login_title: "Login",
            or_continue_with: "Or continue with",
            login_google: "Login with Google",
            login_github: "Login with GitHub",
            system_title: "Complaint Raising Management System",
            system_subtitle: "Efficiently report and track hardware, software, and other issues.",
            email_placeholder: "Email address",
            password_placeholder: "Password",
            forgot_password: "Forgot password?",
            verifying: "Verifying...",
            illust_desc: "Describe Complaint",
            illust_block: "Select Block",
            illust_submit: "Submit Complaint",
            in_progress: "In Progress",
            current_password: "Current Password",
            new_password: "New Password",
            confirm_password: "Confirm New Password",
            change_password: "Change Password",
            repeat_new_password: "Repeat New Password",
            enter_current_password: "Enter your current password",
            password_and_security: "Password & Security",
            // Staff Dashboard
            staff_portal_subtitle: "Dispatcher Hub: Manage your assigned technical tasks and resolutions.",
            assigned_ops_log: "Assigned Operations Log",
            assigned_ops_subtitle: "Live feed of tickets assigned specifically to your resolution queue.",
            my_tasks: "My Tasks",
            view_all: "View All",
            action_center: "Action Center",
            my_profile_lbl: "My Profile",
            profile_update_lbl: "Update your availability",
            secure_cases: "Secure Cases",
            res_protocols: "View resolution protocols",
            res_efficiency: "Resolution Efficiency",
            sla_adherence: "SLA ADHERENCE",
            resolution_score: "SCORE",
            // User Dashboard
            user_portal_subtitle: "Central workspace for orchestrating your service requests and monitoring resolution velocity.",
            ticket_volume: "Ticket Volume",
            awaiting_triage: "Awaiting Triage",
            active_resolution: "In Active Resolution",
            successful_closure: "Successful Closure",
            operation_log: "Operation Log",
            operation_log_subtitle: "Real-time stream of your ticket interactions and state changes.",
            review_archive: "Review Archive",
            strategic_actions: "Strategic Actions",
            instant_res_support: "Instant Resolution Support",
            contact_live_support: "Contact Live Support",
            clear_filter: "Clear Filter",
            filter_timeline: "Filter by Timeline",
            // Complaints List
            support_queue: "Support Queue",
            ticket_details_lbl: "Ticket Details",
            category_lbl: "Category",
            status_lbl: "Status",
            priority_lbl: "Priority",
            date_lbl: "Date",
            assigned_to_lbl: "Assigned To",
            ticket_id: "Ticket ID",
            issue_info: "Issue Information",
            res_timeline: "Resolution Timeline",
            assignment_info_lbl: "Assignment Info",
            waiting_for_staff: "Waiting for Staff",
            sla_est: "Estimated SLA",
            rate_service: "Rate Our Service",
            skip: "Skip",
            submit_feedback: "Submit Feedback",
            on_hold: "On Hold",
            closed: "Closed",
            detail_modal_title: "Ticket Details",
            manage_track_subtitle: "Manage and track resolution progress of your service requests.",
            search_complaints_placeholder: "Search by title, type, or location...",
            no_tickets_found: "No tickets found in this category.",
            tech_staff_desc: "Professional Technical Staff",
            sla_4h: "4 Hours",
            sla_24h: "24 Hours",
            attachments_lbl: "Attachments",
            assign_resolver_lbl: "Assign Resolver",
            select_staff_placeholder: "Select Staff Member",
            assign_specialist_btn: "Assign Specialist",
            add_update_note_placeholder: "Add update note...",
            rate_experience_desc: "How would you describe your experience with the resolution of this ticket?",
            service_feedback_placeholder: "Tell us more about the service...",
            all: "All",
            my_tickets: "My Tickets",
            rating_submitted: "Thank you for your feedback!",
            rating_error: "Error submitting feedback",
            status_updated_success: "Status updated successfully",
            status_update_error: "Error updating status",
            ticket_assigned_success: "Ticket assigned successfully",
            ticket_assign_error: "Error assigning ticket",
            ticket_deleted_success: "Ticket deleted successfully",
            ticket_delete_error: "Error deleting ticket",
            delete_confirm_modal: "Are you sure you want to delete this ticket?",
            staff_portal_subtitle_desc: "Dispatcher Hub: Manage your assigned technical tasks and resolutions.",
            assigned_ops_log_desc: "Live feed of tickets assigned specifically to your resolution queue.",
            task_data_loading: "Crunching task data...",
            on_hold: "On Hold",
            all: "All",
            rate_resolution: "Rate Resolution",
            select_staff_err: "Please select a staff member",
            no_tasks_assigned: "No tasks assigned to your queue.",
            res_efficiency_desc: "Your current average resolution time is 2.4 hours per ticket. Optimal SLA adherence detected.",
            urgent: "Urgent",
            // Master Data
            master_data_subtitle: "System Configuration: Manage foundational data entities and hierarchies.",
            add_new: "Add New",
            manage: "Manage",
            departments: "Departments",
            blocks: "Blocks / Buildings",
            rooms: "Room Numbers",
            roles: "User Roles",
            categories: "Complaint Categories",
            priorities: "Priority Levels",
            field_name: "Name",
            field_short_name: "Short Name / Code",
            field_description: "Description",
            field_parent: "Parent Reference (Optional)",
            programmes: "Programmes",
            delete_confirm: "Delete this record? This action cannot be undone.",
            // Support Center
            support_center_title: "Support & Help Center",
            support_center_subtitle: "Find quick answers in our FAQ or start a live conversation with our technical support team.",
            faq_title: "Frequently Asked Questions",
            chat_title: "Support Chat",
            chat_subtitle: "We reply in minutes",
            type_message: "Type your message...",
            reach_us: "Other Ways to Reach Us",
            faq_1_q: "How do I raise a hardware complaint?",
            faq_1_a: "Go to the Dashboard and click 'Raise New Complaint'. Select 'Hardware' from the category dropdown, provide details, and click submit.",
            faq_2_q: "What is the typical resolution time (SLA)?",
            faq_2_a: "Resolution times vary by priority: Urgent (2h), High (4h), and Medium (24h).",
            faq_3_q: "How can I change my profile information?",
            faq_3_a: "Navigate to 'Settings' from the sidebar. You can update your bio and security preferences there.",
            system_control: "System Control",
            system_status: "System Status",
            maintenance_mode: "Maintenance Mode",
            maintenance_desc: "When maintenance mode is active, users will not be able to raise new complaints.",
            system_on: "System ON",
            system_off: "System OFF (Maintenance)",
            maintenance_active_msg: "System is currently under maintenance. Please try again later.",
            save_status: "Save Status",
            failed_to_update: "Failed to update settings."
        },
        ta: {
            welcome: "வணக்கம்",
            welcome_back: "மீண்டும் வருக!",
            sub_welcome: "உங்கள் புகார்களை மேலாண்மை செய்து நிலையை உடனுக்குடன் அறியுங்கள்.",
            home: "முகப்பு",
            dashboard: "கட்டுப்பாட்டு மையம்",
            complaints: "புகார்கள்",
            reports: "அறிக்கைகள்",
            settings: "அமைப்புகள்",
            raise_complaint: "புகார் அளிக்க",
            profile: "சுயவிவரம்",
            recent_activity: "சமீபத்திய நடவடிக்கைகள்",
            view_all: "அனைத்தையும் காண்க",
            total_raised: "மொத்த புகார்கள்",
            pending: "நிலுவையில்",
            completed: "தீர்க்கப்பட்டது",
            system_audit: "நிர்வாக தணிக்கை பதிவு",
            activity_feed: "நடவடிக்கை ஓட்டம்",
            admin_console: "நிர்வாக முகப்பு",
            admin_subtitle: "புகார்களைக் கண்காணிப்பதற்கும் செயல்பாடுகளை நிர்வகிப்பதற்குமான மையக் கட்டுப்பாட்டு மையம்.",
            global_ticket_stream: "உலகளாவிய புகார்கள்",
            ticket_stream_desc: "அனைத்து துறைகளிலிருந்தும் வரும் நேரடி புகார்களின் ஓட்டம்.",
            operation_controls: "செயல்பாட்டுக் கட்டுப்பாடுகள்",
            manage_users_desc: "பயனர் அணுகல் உரிமைகளை உள்ளமைக்கவும்",
            system_gov: "கணினி நிர்வாகம்",
            system_gov_desc: "தணிக்கை பதிவுகள் மற்றும் பாதுகாப்புக் கொள்கைகள்",
            service_overview: "சேவை கண்ணோட்டம்",
            uptime: "நேரம்",
            load: "சுமை",
            review_all: "அனைத்தையும் சரிபார்க்கவும்",
            logout_confirm: "நீங்கள் உறுதியாக வெளியேற விரும்புகிறீர்களா?",
            current_password: "தற்போதைய கடவுச்சொல்",
            new_password: "புதிய கடவுச்சொல்",
            confirm_password: "புதிய கடவுச்சொல்லை உறுதிப்படுத்து",
            change_password: "கடவுச்சொல்லை மாற்றவும்",
            repeat_new_password: "மீண்டும் புதிய கடவுச்சொல்",
            enter_current_password: "உங்கள் தற்போதைய கடவுச்சொல்லை உள்ளிடவும்",
            password_and_security: "கடவுச்சொல் மற்றும் பாதுகாப்பு",
            // Register Page
            create_account: "கணக்கை உருவாக்கவும்",
            join_system: "புகார் மேலாண்மை அமைப்பில் சேரவும்",
            already_have_account: "ஏற்கனவே கணக்கு உள்ளதா?",
            login_here: "இங்கே உள்நுழையவும்",
            // General
            welcome: "வணக்கம்",
            welcome_back: "மீண்டும் வருக!",
            sub_welcome: "உங்கள் புகார்களை மேலாண்மை செய்து நிலையை உடனுக்குடன் அறியுங்கள்.",
            email_lbl: "மின்னஞ்சல் முகவரி",
            password_lbl: "கடவுச்சொல்",
            login_failed: "உள்நுழைவு தோல்வியடைந்தது",
            login_hint: "கணக்கு வேண்டுமா? தயவுசெய்து உங்கள் கணினி நிர்வாகியைத் தொடர்பு கொள்ளவும்.",
            login_title: "உள்நுழைவு",
            or_continue_with: "அல்லது இதனுடன் தொடரவும்",
            login_google: "Google மூலம் உள்நுழையவும்",
            login_github: "GitHub மூலம் உள்நுழையவும்",
            system_title: "புகார் மேலாண்மை அமைப்பு",
            system_subtitle: "வன்பொருள், மென்பொருள் மற்றும் பிற சிக்கல்களைத் திறம்படப் புகாரளிக்கவும் மற்றும் கண்காணிக்கவும்.",
            email_placeholder: "மின்னஞ்சல் முகவரி",
            password_placeholder: "கடவுச்சொல்",
            forgot_password: "கடவுச்சொல்லை மறந்துவிட்டீர்களா?",
            verifying: "சரிபார்க்கப்படுகிறது...",
            illust_desc: "புகாரை விவரிக்கவும்",
            illust_block: "பிளாக்கைத் தேர்ந்தெடுக்கவும்",
            illust_submit: "புகாரைச் சமர்ப்பிக்கவும்",
            in_progress: "செயல்பாட்டில்",
            completed: "முடிந்தது",
            notifications: "அறிவிப்புகள்",
            no_notifications: "புதிய அறிவிப்புகள் இல்லை.",
            search_placeholder: "புகார்களைத் தேடுங்கள்...",
            my_quick_stats: "எனது புள்ளிவிவரங்கள்",
            profile_settings: "சுயவிவர அமைப்புகள்",
            display_language: "காட்சி மற்றும் மொழி",
            logout: "வெளியேறு",
            save_changes: "மாற்றங்களைச் சேமி",
            theme_mode: "தீம் பயன்முறை",
            theme_desc: "இருண்ட மற்றும் ஒளி பயன்முறைக்கு இடையில் மாறவும்.",
            app_language: "பயன்பாட்டு மொழி",
            lang_desc: "உங்களுக்கு விருப்பமான மொழியைத் தேர்ந்தெடுக்கவும்.",
            profile_info: "சுயவிவரத் தகவல்",
            full_name: "முழு பெயர்",
            email_address: "மின்னஞ்சல் முகவரி",
            bio: "விவரம்",
            change_photo: "புகைப்படத்தை மாற்றவும்",
            remove_photo: "புகைப்படத்தை நீக்கவும்",
            upload_photo: "புகைப்படத்தைப் பதிவேற்றவும்",
            remove_bio: "விவரத்தை நீக்கவும்",
            email_notif: "மின்னஞ்சல் அறிவிப்புகள்",
            notif_desc: "உங்கள் புகார்கள் குறித்த புதுப்பிப்புகளை மின்னஞ்சல் மூலம் பெறுங்கள்.",
            push_notif: "புஷ் அறிவிப்புகள்",
            push_desc: "உங்கள் உலாவியில் நிகழ்நேர விழிப்பூட்டல்களைப் பெறுங்கள்.",
            raise_new_title: "புதிய புகாரைப் பதிவு செய்யவும்",
            subject_title: "தலைப்பு / பொருள்",
            subject_placeholder: "எ.கா. அச்சுப்பொறி வேலை செய்யவில்லை",
            complaint_type: "புகார் வகை",
            location_block: "இடம் / கட்டிடம்",
            location_placeholder: "எ.கா. பிளாக் A-102",
            department: "துறை பெயர்",
            dept_placeholder: "எ.கா. தகவல் தொழில்நுட்பத் துறை",
            room_no: "அறை எண்",
            room_placeholder: "எ.கா. 101",
            description: "விவரம்",
            desc_placeholder: "பிரச்சினையை விரிவாக விளக்கவும்...",
            submit_complaint: "புகாரைச் சமர்ப்பிக்கவும்",
            back_to_dashboard: "தகவல் பலகைக்குத் திரும்பு",
            raise_new_desc: "உங்கள் சிக்கலை எங்கள் தொழில்நுட்ப ஊழியர்கள் விரைவாகத் தீர்க்க விரிவான தகவலை வழங்கவும்.",
            ticket_id_lbl: "புகார் எண்",
            attachment_lbl: "இணைப்பு (படங்கள்/PDF)",
            drop_file_hint: "கோப்பை இங்கே விடவும் அல்லது தேட கிளிக் செய்யவும்",
            max_file_size: "அதிகபட்ச அளவு: 5MB • வடிவங்கள்: JPG, PNG, PDF",
            processing: "செயலாக்கப்படுகிறது...",
            submit_request: "ஆதரவு கோரிக்கையைச் சமர்ப்பிக்கவும்",
            response_time_sla: "பதிலளிக்கும் கால அளவு (SLA)",
            urgent_priority: "அவசர முன்னுரிமை",
            high_priority: "உயர் முன்னுரிமை",
            medium_priority: "நடுத்தர முன்னுரிமை",
            quality_guarantee: "தர உத்தரவாதம்",
            quality_verif: "புகாரளித்த பயனரின் தீர்வு சரிபார்ப்பு.",
            quality_escalation: "தாமதமான கோரிக்கைகளுக்குத் தானியங்கி ஏறுவரிசை.",
            quality_log: "ஒவ்வொரு செயலுக்கும் வெளிப்படையான செயல்பாட்டுப் பதிவு.",
            hours: "மணிநேரம்",
            all_complaints: "அனைத்து புகார்கள்",
            details: "விவரங்கள்",
            linked_to: "இணைக்கப்பட்டுள்ளது",
            quick_setup: "விரைவான அமைப்பு",
            records: "பதிவுகள்",
            unlinked: "இணைக்கப்படவில்லை",
            alias: "மாற்றுப் பெயர்",
            save_updates: "புதுப்பிப்புகளைச் சேமி",
            confirm_registration: "பதிவை உறுதிப்படுத்து",
            edit_item: "திருத்து",
            add_item: "சேர்",
            room_number_lbl: "அறை எண்",
            block_name_lbl: "பிளாக் பெயர் / குறியீடு",
            programme_name_lbl: "பாடப்பிரிவு பெயர்",
            optional_desc_lbl: "கூடுதல் விவரம்",
            select_parent: "பெற்றோரைத் தேர்ந்தெடுக்கவும்...",
            no_complaints_found: "புகார்கள் எதுவும் இல்லை",
            id: "எண்",
            type: "வகை",
            location: "இடம்",
            status: "நிலை",
            assigned_to: "ஒதுக்கப்பட்டவர்",
            date: "தேதி",
            actions: "செயல்கள்",
            system_reports: "கணினி அறிக்கைகள்",
            new_report: "புதிய அறிக்கை",
            monthly_report: "மாதாந்திர தீர்வு அறிக்கை",
            dept_efficiency: "துறை வாரியான செயல்திறன்",
            audit_summary: "வன்பொருள் தணிக்கை சுருக்கம்",
            user_survey: "பயனர் திருப்தி ஆய்வு",
            generated_on: "உருவாக்கப்பட்டது",
            format: "வடிவம்",
            total_complaints: "மொத்த புகார்கள்",
            active_users: "செயலில் உள்ள பயனர்கள்",
            system_audit: "அமைப்பு தணிக்கை பதிவுகள்",
            average_rating: "சராசரி மதிப்பீடு",
            // Sidebar & Nav
            master_data: "முதன்மைத் தரவு",
            audit_logs: "தணிக்கை பதிவுகள்",
            user_management: "பயனர் மேலாண்மை",
            // Dashboards
            staff_portal: "பணியாளர் தீர்வு மையம்",
            support_workspace: "ஆதரவு பணியிடம்",
            admin_control: "நிர்வாகக் கட்டுப்பாட்டு மையம்",
            admin_console: "நிர்வாக முகப்பு",
            admin_subtitle: "புகார்களைக் கண்காணிப்பதற்கும் செயல்பாடுகளை நிர்வகிப்பதற்குமான மையக் கட்டுப்பாட்டு மையம்.",
            global_ticket_stream: "உலகளாவிய புகார்கள்",
            ticket_stream_desc: "அனைத்து துறைகளிலிருந்தும் வரும் நேரடி புகார்களின் ஓட்டம்.",
            operation_controls: "செயல்பாட்டுக் கட்டுப்பாடுகள்",
            manage_users_desc: "பயனர் அணுகல் உரிமைகளை உள்ளமைக்கவும்",
            system_gov: "கணினி நிர்வாகம்",
            system_gov_desc: "தணிக்கை பதிவுகள் மற்றும் பாதுகாப்புக் கொள்கைகள்",
            service_overview: "சேவை கண்ணோட்டம்",
            uptime: "நேரம்",
            load: "சுமை",
            review_all: "அனைத்தையும் சரிபார்க்கவும்",
            fetching_ticket_data: "தரவு சேகரிக்கப்படுகிறது...",
            no_activity_recorded: "நடவடிக்கைகள் எதுவும் இல்லை.",
            admin_perf_desc: "கணினியின் செயல்பாடு தற்போது சிறப்பாக உள்ளது. 98.4% புகார்கள் சரியான நேரத்தில் தீர்க்கப்பட்டுள்ளன.",
            user_intelligence: "பயனர் தகவல் மையம்",
            auth_with_server: "சர்வர் உடன் இணைக்கப்படுகிறது...",
            no_segment_tickets: "இந்த பகுதியில் புகார்கள் எதுவும் இல்லை.",
            init_first_ticket: "உங்கள் முதல் புகாரை பதிவு செய்யுங்கள்",
            user_res_desc: "முக்கியமான வன்பொருள் சிக்கல்களை உடனடியாக தீர்க்க எங்கள் தொழில்நுட்ப குழுவை அணுகவும்.",
            raise_ticket: "புகார் பதிவு",
            edit_profile_btn: "சுயவிவரம் திருத்து",
            need_help: "உதவி தேவையா?",
            raise_ticket_desc: "புதிய புகாரை உருவாக்கவும்",
            track_res_desc: "தீர்வு நிலையைக் கண்காணிக்கவும்",
            manage_personal_desc: "உங்கள் தகவலை நிர்வகிக்கவும்",
            read_faq_desc: "கேள்விகள் மற்றும் வழிகாட்டிகளைப் படிக்கவும்",
            master_control: "தலைமை கட்டுப்பாட்டு மையம்",
            system_load: "கணினி சுமை",
            capacity_analysis: "திறன் பகுப்பாய்வு",
            activity_feed: "நடவடிக்கை ஓட்டம்",
            // User Management
            global_directory: "உலகளாவிய பயனர் பட்டியல்",
            system_user_mgmt: "கணினி பயனர் மேலாண்மை",
            add_member: "உறுப்பினரைச் சேர்",
            edit_profile: "சுயவிவரத்தைத் திருத்து",
            identity: "அடையாளம்",
            auth_role: "அதிகாரப் பங்கு",
            options: "விருப்பங்கள்",
            user_mgmt_desc: "அனைத்து ஊழியர்கள் மற்றும் உறுப்பினர்களின் கணக்குகளை நிர்வகிக்கும் மத்தியக் கட்டுப்பாடு.",
            search_directory: "தேடல்...",
            identity_name: "முழு பெயர்",
            phone_optional: "தொலைபேசி (விருப்பத்தேர்வு)",
            email_access: "மின்னஞ்சல் அணுகல்",
            security_role: "பாதுகாப்பு பங்கு",
            assigned_dept: "ஒதுக்கப்பட்ட துறை",
            temp_password: "தற்காலிக கடவுச்சொல்",
            update_directory: "பட்டியலைப் புதுப்பி",
            finalize_reg: "பதிவை முடி",
            // Forms
            incident_date: "சம்பவத் தேதி",
            priority: "முன்னுரிமை",
            emergency: "அவசரம்",
            high: "அதிகம்",
            medium: "நடுத்தரம்",
            low: "குறைவு",
            block: "பிளாக்",
            room: "அறை",
            // Broadcast
            broadcast: "அறிவிப்பு",
            system_broadcast: "கணினி அறிவிப்பு",
            send_to_all: "அனைத்து பயனர்களுக்கும் அனுப்பு",
            broadcast_desc: "கணினியில் உள்ள அனைத்து பயனர்களுக்கும் செய்தி அனுப்பவும்.",
            message_content: "செய்தி உள்ளடக்கம்",
            message_placeholder: "உங்கள் செய்தியை இங்கே தட்டச்சு செய்யவும்...",
            send_to_all_users: "அனைத்து பயனர்களுக்கும் அனுப்பு",
            // Security
            security: "பாதுகாப்பு",
            current_password: "தற்போதைய கடவுச்சொல்",
            new_password: "புதிய கடவுச்சொல்",
            confirm_password: "புதிய கடவுச்சொல்லை உறுதிப்படுத்து",
            // Common Buttons
            cancel: "ரத்து செய்",
            delete: "நீக்கு",
            update: "புதுப்பி",
            submit: "சமர்ப்பி",
            // Home Page
            welcome_home: "முகப்பிற்கு வருக",
            hero_sub_staff: "கணினி சேவை கட்டளை",
            hero_sub_user: "தடையற்ற பிரச்சினை தீர்வு மையம்",
            hero_title_staff: "தீர்வு மையம். நிபுணர் நடவடிக்கை.",
            hero_title_user: "புத்திசாலித்தனமாகப் புகாரளிக்கவும். விரைவாகத் தீர்க்கவும்.",
            hero_desc_staff: "உங்களுக்கு ஒதுக்கப்பட்ட வழக்குகளில் கவனம் செலுத்துங்கள், முன்னுரிமைகளைப் புதுப்பிக்கவும் மற்றும் உயர் தரத்தைப் பராமரிக்கவும்.",
            hero_desc_user: "உங்கள் தொழில்நுட்பக் கவலைகளை நொடிகளில் சமர்ப்பிக்கவும். எங்கள் தானியங்கி அமைப்பு உங்கள் புகாரை உரிய குழுவிற்கு அனுப்பும்.",
            manage_assigned: "ஒதுக்கப்பட்ட வழக்குகளை நிர்வகி",
            submit_support: "ஆதரவு கோரிக்கையை சமர்ப்பி",
            adv_monitor: "மேம்பட்ட கண்காணிப்பு",
            monitor_desc: "உங்கள் பணியிட செயல்திறன் குறித்த நிகழ்நேர பகுப்பாய்வு மற்றும் அளவீடுகள்.",
            case_history: "வழக்கு வரலாறு",
            case_desc: "அனைத்து வரலாற்று சமர்ப்பிப்புகள் மற்றும் தற்போதைய செயல்பாடுகளை உலாவவும்.",
            live_status: "நேரடி நிலை",
            track_progress: "முன்னேற்றத்தைக் கண்காணிக்கவும்",
            tech_tier: "தொழில்நுட்ப நிலை",
            // Audit Logs
            action_type: "செயல் வகை",
            performer: "செய்தவர்",
            ip_address: "ஐபி முகவரி",
            timestamp: "நேரம்",
            audit_action: "செயல்",
            audit_details: "விவரங்கள்",
            all_actions: "அனைத்து செயல்கள்",
            all_dates: "அனைத்து தேதிகள்",
            today: "இன்று",
            last_7_days: "கடந்த 7 நாட்கள்",
            refresh: "புதுப்பி",
            export_csv: "CSV ஏற்றுமதி",
            // Dashboards General
            total_raised: "மொத்த புகார்கள்",
            pending: "நிலுவையில் உள்ளது",
            completed: "தீர்க்கப்பட்டது",
            in_progress: "செயல்பாட்டில் உள்ளது",
            admin_console: "நிர்வாக மையம்",
            admin_subtitle: "புகார்கள், பயனர்கள் மற்றும் கணினி அளவுருக்களை நிர்வகிக்கவும்.",
            global_ticket_stream: "உலகளாவிய புகார்களின் ஓட்டம்",
            ticket_stream_desc: "அனைத்து துறைகளிலும் பதிவு செய்யப்பட்ட புகார்களின் நேரடி விவரங்கள்.",
            operation_controls: "செயல்பாட்டு கட்டுப்பாடுகள்",
            system_gov: "கணினி ஆளுமை",
            system_gov_desc: "பங்குகள், பிரிவுகள் மற்றும் இடங்களை நிர்வகிக்கவும்.",
            service_overview: "சேவை கண்ணோட்டம்",
            uptime: "செயல் நேரம்",
            load: "சுமை",
            security_guard_active: "பாதுகாப்பு காவலர் செயலில் உள்ளார்",
            infrastructure_integrity: "உள்கட்டமைப்பு ஒருமைப்பாடு சரிபார்க்கப்பட்டது. தரவு பரிமாற்றத்திற்கான பாதுகாப்பு இயக்கப்பட்டுள்ளது.",
            generate_report: "முடிவு அறிக்கையை உருவாக்கவும்",
            report_subject: "அறிக்கையின் பொருள்",
            recipient_email: "பெறுநர் மின்னஞ்சல்",
            dispatch_report: "அதிகாரப்பூர்வ அறிக்கையை அனுப்பு",
            distribution_trends: "விநியோக போக்குகள்",
            avg_resolution: "சராசரி தீர்வு நேரம்",
            server_latency: "கணினி தாமதம்",
            system_audit: "கணினி தணிக்கை",
            security_trail_desc: "பாதுகாப்பு தடம் மற்றும் நிர்வாக நடவடிக்கைகள் கண்காணிப்பு.",
            satisfaction_rate: "திருப்தி விகிதம்",
            satisfaction_rate_lbl: "பயனர் கருத்து அடிப்படையிலான திருப்தி விகிதம்.",
            fetching_data: "தரவைப் பெறுகிறது...",
            no_activity: "நடவடிக்கைகள் எதுவும் இல்லை.",
            volume_vs_last_month: "கடந்த மாதத்துடன் ஒப்பிடும்போது",
            priority_resolution: "முன்னுரிமை தீர்வு",
            active: "செயலில்",
            minimal: "குறைந்தபட்சம்",
            optimal: "சிறந்தது",
            low_cap: "குறைவு",
            security_trail_extended_desc: "பாதுகாப்பு தடம் மற்றும் நிர்வாக நடவடிக்கைகளின் நேரடி கண்காணிப்பு.",
            fetching_security_logs: "பாதுகாப்பு பதிவுகளைப் பெறுகிறது...",
            no_audit_logs_found: "தணிக்கைப் பதிவுகள் எதுவும் இல்லை",
            no_security_events_match: "உங்கள் வடிகட்டி அளவுகோல்களுடன் எந்த பாதுகாப்பு நிகழ்வுகளும் பொருந்தவில்லை.",
            system_service_account: "சேவைக் கணக்கு",
            internal_ip: "உள்",
            unknown_user: "தெரியாத பயனர்",
            management_reports: "மேலாண்மை அறிக்கைகள்",
            official_res_documents_desc: "உங்கள் அதிகாரப்பூர்வமான தீர்வு ஆவணங்கள் மற்றும் சான்றிதழ்களை அணுகவும்.",
            reports_count: "அறிக்கைகள்",
            send_res_report: "தீர்வு அறிக்கையை அனுப்பு",
            admin_panel: "நிர்வாகக் குழு",
            select_task: "பணியைத் தேர்ந்தெடுக்கவும்",
            select_completed_complaint: "முடிந்த புகாரைத் தேர்ந்தெடுக்கவும்...",
            report_sent_to_user_desc: "இந்த அறிக்கை பயனரின் கட்டுப்பாட்டு மையத்திற்கு அனுப்பப்படும்.",
            report_overview: "அறிக்கை மேலோட்டம்",
            certifies_resolution_desc: "இந்த ஆவணம் கீழே விவரிக்கப்பட்டுள்ள சேவை கோரிக்கையின் வெற்றிகரமான தீர்வை உறுதிப்படுத்துகிறது.",
            recipient_info: "பெறுநர் தகவல்",
            task_reference: "பணி விவரக்குறிப்பு",
            date_generated: "உருவாக்கப்பட்ட தேதி",
            verified_by: "சரிபார்க்கப்பட்டது",
            download_official_pdf: "அதிகாரப்பூர்வ PDF பதிவிறக்கவும்",
            // Staff Dashboard
            staff_portal_subtitle: "பணிப்பாளர் மையம்: உங்களுக்கு ஒதுக்கப்பட்ட பணிகளை நிர்வகிக்கவும்.",
            assigned_ops_log: "ஒதுக்கப்பட்ட செயல்பாட்டு பதிவு",
            assigned_ops_subtitle: "உங்களுக்கு ஒதுக்கப்பட்ட புகார்களின் நேரடி விவரங்கள்.",
            my_tasks: "எனது பணிகள்",
            view_all: "அனைத்தையும் பார்",
            action_center: "செயல் மையம்",
            my_profile_lbl: "எனது சுயவிவரம்",
            profile_update_lbl: "உங்கள் இருப்பைப் புதுப்பிக்கவும்",
            secure_cases: "பாதுகாப்பான வழக்குகள்",
            res_protocols: "தீர்வு நெறிமுறைகளைக் காண்க",
            res_efficiency: "தீர்வுத் திறன்",
            sla_adherence: "சமரச ஒப்பந்தம்",
            resolution_score: "மதிப்பெண்",
            // User Dashboard
            user_portal_subtitle: "உங்கள் சேவை கோரிக்கைகளை நிர்வகிக்கும் மத்திய பணியிடம்.",
            ticket_volume: "புகார் எண்ணிக்கை",
            awaiting_triage: "வகைப்படுத்த காத்திருக்கிறது",
            active_resolution: "செயலில் உள்ள தீர்வு",
            successful_closure: "வெற்றிகரமான முடிவு",
            operation_log: "செயல்பாட்டு பதிவு",
            operation_log_subtitle: "உங்கள் புகார்களின் நேரடி மாற்றங்கள் மற்றும் தொடர்புகள்.",
            review_archive: "காப்பகத்தை மதிப்பாய்வு செய்க",
            strategic_actions: "மூலோபாய செயல்கள்",
            instant_res_support: "உடனடி தீர்வு ஆதரவு",
            contact_live_support: "நேரடி ஆதரவைத் தொடர்பு கொள்ளவும்",
            clear_filter: "வடிகட்டியை அகற்று",
            filter_timeline: "காலவரிசைப்படி வடிகட்டு",
            // Complaints List
            support_queue: "ஆதரவு வரிசை",
            ticket_details_lbl: "புகார் விவரங்கள்",
            category_lbl: "வகை",
            status_lbl: "நிலை",
            priority_lbl: "முன்னுரிமை",
            date_lbl: "தேதி",
            assigned_to_lbl: "ஒதுக்கீடு",
            ticket_id: "புகார் எண்",
            issue_info: "பிரச்சினை தகவல்",
            res_timeline: "தீர்வு காலவரிசை",
            assignment_info_lbl: "ஒதுக்கீடு தகவல்",
            waiting_for_staff: "பணியாளருக்காகக் காத்திருக்கிறது",
            sla_est: "மதிப்பிடப்பட்ட தீர்வு நேரம்",
            rate_service: "எங்கள் சேவையை மதிப்பிடுங்கள்",
            skip: "தவிர்",
            submit_feedback: "கருத்துகளைச் சமர்ப்பிக்கவும்",
            on_hold: "நிறுத்தி வைக்கப்பட்டுள்ளது",
            closed: "மூடப்பட்டது",
            detail_modal_title: "புகார் விவரங்கள்",
            manage_track_subtitle: "உங்கள் சேவை கோரிக்கைகளை நிர்வகிக்கும் மற்றும் கண்காணிக்கும் தளம்.",
            search_complaints_placeholder: "தலைப்பு, வகை அல்லது இடம் மூலம் தேடுங்கள்...",
            no_tickets_found: "இந்த பிரிவில் புகார்கள் எதுவும் இல்லை.",
            tech_staff_desc: "தொழில்நுட்ப வல்லுநர்",
            sla_4h: "4 மணிநேரம்",
            sla_24h: "24 மணிநேரம்",
            attachments_lbl: "இணைப்புகள்",
            assign_resolver_lbl: "தீர்வாளரை ஒதுக்கு",
            select_staff_placeholder: "பணியாளரைத் தேர்ந்தெடுக்கவும்",
            assign_specialist_btn: "வல்லுநரை ஒதுக்கு",
            add_update_note_placeholder: "புதுப்பிப்பு குறிப்பைச் சேர்க்கவும்...",
            rate_experience_desc: "இந்த புகார் தீர்க்கப்பட்ட விதம் உங்களுக்கு திருப்தியாக உள்ளதா?",
            service_feedback_placeholder: "சேவையை பற்றி விரிவாக கூறவும்...",
            all: "அனைத்தும்",
            my_tickets: "எனது புகார்கள்",
            rating_submitted: "உங்கள் கருத்திற்கு நன்றி!",
            rating_error: "கருத்தைச் சமர்ப்பிப்பதில் பிழை",
            status_updated_success: "நிலை வெற்றிகரமாக புதுப்பிக்கப்பட்டது",
            status_update_error: "நிலையைப் புதுப்பிப்பதில் பிழை",
            ticket_assigned_success: "புகார் வெற்றிகரமாக ஒதுக்கப்பட்டது",
            ticket_assign_error: "புகாரை ஒதுக்குவதில் பிழை",
            ticket_deleted_success: "புகார் வெற்றிகரமாக நீக்கப்பட்டது",
            ticket_delete_error: "புகாரை நீக்குவதில் பிழை",
            delete_confirm_modal: "நிச்சயமாக இந்தப் புகாரை நீக்க விரும்புகிறீர்களா?",
            staff_portal_subtitle_desc: "பணிப்பாளர் மையம்: உங்களுக்கு ஒதுக்கப்பட்ட பணிகளை நிர்வகிக்கவும்.",
            assigned_ops_log_desc: "உங்களுக்கு ஒதுக்கப்பட்ட புகார்களின் நேரடி விவரங்கள்.",
            task_data_loading: "தரவு ஏற்றப்படுகிறது...",
            no_tasks_assigned: "உங்களுக்கு ஒதுக்கப்பட்ட பணிகள் எதுவும் இல்லை.",
            res_efficiency_desc: "உங்கள் தற்போதைய சராசரி தீர்வு நேரம் 2.4 மணிநேரம். சிறந்த முறையில் செயல்படுகிறீர்கள்.",
            urgent: "அவசரம்",
            resolved: "தீர்க்கப்பட்டது",
            pending: "நிலுவையில்",
            on_hold: "நிறுத்தி வைக்கப்பட்டுள்ளது",
            all: "அனைத்தும்",
            rate_resolution: "தீர்வை மதிப்பிடுங்கள்",
            select_staff_err: "பணியாளரைத் தேர்ந்தெடுக்கவும்",
            in_progress: "செயல்பாட்டில் உள்ளது",
            closed: "மூடப்பட்டது",
            // Master Data
            master_data_subtitle: "கணினி உள்ளமைப்பு: அடிப்படை தரவு மற்றும் படிநிலைகளை நிர்வகிக்கவும்.",
            add_new: "புதிதாக சேர்",
            manage: "நிர்வகி",
            departments: "துறைகள்",
            blocks: "தொகுதிகள் / கட்டிடங்கள்",
            rooms: "அறை எண்கள்",
            roles: "பயனர் பாத்திரங்கள்",
            categories: "புகார் வகைகள்",
            priorities: "முன்னுரிமை நிலைகள்",
            field_name: "பெயர்",
            field_short_name: "குறுகிய பெயர் / குறியீடு",
            field_description: "விளக்கம்",
            field_parent: "பெற்றோர் குறிப்பு (விருப்பத்தேர்வு)",
            programmes: "பாடப்பிரிவுகள்",
            delete_confirm: "இந்த பதிவை நீக்கவா? இந்த செயலை மாற்ற முடியாது.",
            // Support Center
            support_center_title: "ஆதரவு மற்றும் உதவி மையம்",
            support_center_subtitle: "எங்கள் கேள்விகளில் விரைவான பதில்களைக் கண்டறியவும் அல்லது எங்கள் தொழில்நுட்ப ஆதரவு குழுவுடன் நேரடி உரையாடலைத் தொடங்கவும்.",
            faq_title: "அடிக்கடி கேட்கப்படும் கேள்விகள்",
            chat_title: "ஆதரவு அரட்டை",
            chat_subtitle: "நாங்கள் நிமிடங்களில் பதிலளிக்கிறோம்",
            type_message: "உங்கள் செய்தியைத் தட்டச்சு செய்க...",
            reach_us: "எங்களைத் தொடர்பு கொள்ள பிற வழிகள்",
            faq_1_q: "வன்பொருள் புகாரை நான் எவ்வாறு எழுப்புவது?",
            faq_1_a: "டாஷ்போர்டிற்குச் சென்று 'புதிய புகாரை எழுப்பு' என்பதைக் கிளிக் செய்யவும். வகை கீழ்தோன்றலில் இருந்து 'வன்பொருள்' என்பதைத் தேர்ந்தெடுக்கவும், விவரங்களை வழங்கவும் மற்றும் சமர்ப்பிக்கவும் கிளிக் செய்யவும்.",
            faq_2_q: "வழக்கமான தீர்வு நேரம் (SLA) என்ன?",
            faq_2_a: "முன்னுரிமையின் அடிப்படையில் தீர்வு நேரங்கள் மாறுபடும்: அவசரம் (2ம), உயர் (4ம), மற்றும் நடுத்தரம் (24ம).",
            faq_3_q: "எனது சுயவிவரத் தகவலை நான் எவ்வாறு மாற்றுவது?",
            faq_3_a: "சைட்பாரில் இருந்து 'அமைப்புகளுக்கு' செல்லவும். உங்கள் பயோ மற்றும் பாதுகாப்பு விருப்பங்களை அங்கு புதுப்பிக்கலாம்.",
            system_control: "கணினி கட்டுப்பாடு",
            system_status: "கணினி நிலை",
            maintenance_mode: "பராமரிப்பு முறை",
            maintenance_desc: "பராமரிப்பு முறை செயல்பாட்டில் இருக்கும்போது, பயனர்கள் புதிய புகார்களைப் பதிவு செய்ய முடியாது.",
            system_on: "இயக்கம் (ON)",
            system_off: "பராமரிப்பு (OFF)",
            maintenance_active_msg: "கணினி தற்போது பராமரிப்பில் உள்ளது. தயவுசெய்து சிறிது நேரம் கழித்து மீண்டும் முயற்சிக்கவும்.",
            save_status: "நிலையைச் சேமி",
            failed_to_update: "அமைப்புகளைப் புதுப்பிப்பதில் பிழை ஏற்பட்டது."
        }
    };

    const t = (key) => translations[language][key] || key;

    return (
        <AppContext.Provider value={{
            theme, setTheme, toggleTheme,
            language, setLanguage,
            notificationsEnabled, setNotificationsEnabled,
            isSidebarOpen, setIsSidebarOpen, toggleSidebar,
            t
        }}>
            {children}
        </AppContext.Provider>
    );
};

export const useApp = () => useContext(AppContext);
